/**
 * The panel's half of the Smolit protocol.
 *
 * The server streams server-sent events and the panel renders them as they
 * arrive. `EventSource` cannot be used: it only issues GET requests and cannot
 * carry an Authorization header, and this is a POST with the conversation in
 * its body. So the stream is read off `fetch` by hand, which is a few more
 * lines and works with the token-based sessions ADR-0008 settled on.
 */

import { api } from './api';

export interface ConfirmationCard {
  title: string;
  summary: string;
  details: Array<{ label: string; value: string }>;
  confirmLabel: string;
  tone?: 'default' | 'caution';
}

/** Mirrors `TurnEvent` in `@openroad/smolit-core`. */
export type TurnEvent =
  | { type: 'text'; text: string }
  | { type: 'tool'; phase: 'start' | 'end'; name: string; label: string }
  | { type: 'attachment'; kind: string; data: unknown }
  | { type: 'confirmation'; approvalId: string; toolName: string; card: ConfirmationCard }
  | { type: 'error'; message: string }
  | { type: 'done'; stop: string; messages: unknown[] };

export interface SmolitConfig {
  provider: string;
  model: string;
  offline: boolean;
  toolCount: number;
  confirmingTools: string[];
  pending: Array<{ id: string; toolName: string; card: ConfirmationCard }>;
}

export async function fetchSmolitConfig(signal?: AbortSignal): Promise<SmolitConfig> {
  const response = await api.authorizedFetch('/smolit/config', { signal });
  if (!response.ok) throw new Error('Could not reach Smolit.');
  return (await response.json()) as SmolitConfig;
}

/**
 * Post a turn and yield its events as they arrive.
 *
 * The chunk boundary and the event boundary are different things — a chunk can
 * split an event in half, or carry three of them — so a buffer holds whatever
 * has not reached a blank line yet.
 */
export async function* streamTurn(
  path: '/smolit/chat' | '/smolit/approve',
  body: unknown,
  signal?: AbortSignal,
): AsyncGenerator<TurnEvent> {
  const response = await api.authorizedFetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok || !response.body) {
    let message = 'Smolit could not answer that.';
    try {
      const parsed = (await response.json()) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      // A non-JSON failure body tells us nothing more than the status did.
    }

    /**
     * The error, and then nothing — deliberately no `done`.
     *
     * A `done` is the server saying it saw the turn through, and these failures
     * are the opposite: a 401, a 429, a 500 raised before the handler ran. The
     * approval was never claimed. Ending the stream with one anyway told the
     * panel the decision had been processed, so a rate-limited approval read
     * back as "Approved." while it sat pending on the server — and its empty
     * `messages` wiped the transcript the model works from as it went.
     */
    yield { type: 'error', message };
    return;
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += value;

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = parseFrame(frame);
        if (event) yield event;
        boundary = buffer.indexOf('\n\n');
      }
    }

    // A stream that ended without its trailing blank line still has one event.
    const last = parseFrame(buffer);
    if (last) yield last;
  } finally {
    // Cancelling matters when the member closes the panel mid-answer: without
    // it the request keeps running and the tokens keep being spent.
    await reader.cancel().catch(() => undefined);
  }
}

function parseFrame(frame: string): TurnEvent | null {
  const line = frame.split('\n').find((candidate) => candidate.startsWith('data: '));
  if (!line) return null;
  try {
    return JSON.parse(line.slice(6)) as TurnEvent;
  } catch {
    return null;
  }
}
